import {
    Directive,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { of } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ActionSheet } from '../../../models/common';
import { ActionSheetService } from '../../../services/action-sheet/action-sheet.service';
import { ResponsiveService } from '../../../services/responsive/responsive.service';
import { CheckableRowsService } from '../../../services/table/checkable-rows/checkable-rows.service';
import { ExpandableRowsService } from '../../../services/table/expandable-rows/expandable-rows.service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { Utilities } from '../../../utilities/utilities.helper';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';

@LifecycleAware()
@Directive()
export abstract class LsqExpandableDataRowComponent<RowType, InnerTableType>
    implements OnInit, OnChanges, OnDestroy {
    @ViewChild('actionSheetTemplate') public actionSheetTemplate?: TemplateRef<any>;
    @Input() public row: RowType;
    @Input() public rowIsExpandable = true;
    @Input() public rowsAreCheckable = true;
    @Input() public hideCheckBox = true;
    @Input() public checkableRowsService: CheckableRowsService;
    @Input() public expandableRowsService: ExpandableRowsService<RowType>;
    @Input() public data?: any;
    @Input() public rowPrimaryKey?: string;
    @Input() public showInnerHeader = false;
    @Input() public fromDateFilter?: Date;
    @Input() public toDateFilter?: Date;

    @Output() public check = new EventEmitter<boolean>();
    @Output() public dynamicLinkClick = new EventEmitter<{ row: RowType }>();

    // Expandable row data.
    public dataSourceRow: LsqListSource<RowType>;

    // Inner table data.
    public dataSourceInner: LsqListSource<InnerTableType>;

    public isExpanded = false;
    public isChecked = false;
    public innerTableIsLoading = true;
    public mobileDrawerRows: InnerTableType[];
    public isSmallDevice: boolean;
    public actionSheet: ActionSheet;
    public actionSheetIsOpen = false;

    constructor(
        public responsiveService: ResponsiveService,
        public actionSheetService: ActionSheetService
    ) {}

    public ngOnChanges(): void {
        this.dataSourceRow = new LsqListSource<RowType>({
            rows$: of([this.row] as [RowType])
        });
    }

    public ngOnInit(): void {
        if (this.checkableRowsService) {
            this.checkableRowsService.checkedRowsSubject.pipe(forLifeOf(this)).subscribe(() => {
                this.isChecked = this.checkableRowsService.isRowChecked(
                    this.row[this.rowPrimaryKey]
                );
            });
        }

        this.expandableRowsService.rowsExpandedSubject.pipe(forLifeOf(this)).subscribe(() => {
            const shouldExpand = this.expandableRowsService.isRowExpanded(this.row);
            if (!this.isExpanded && shouldExpand) {
                this.toggleExpanded(shouldExpand, false);
            }
            this.isExpanded = shouldExpand;
        });

        this.responsiveService.smBelow.pipe(forLifeOf(this)).subscribe((isSmBelow) => {
            if (isSmBelow && this.expandableRowsService) {
                // This fixes the issue of the action sheet appearing when resizing to mobile.
                // However, I believe this might be a bandaid fix, and that there are some larger
                // underlying issues with the rxjs in the expandable row service.
                this.expandableRowsService.collapseAll();
            }
            this.isSmallDevice = isSmBelow;
        });

        // When the action sheet is closed (`null` is streamed), tell the expandable row to collapse.
        this.actionSheetService.actionSheetStream
            .pipe(
                forLifeOf(this),
                filter((actionSheet) => actionSheet === null)
            )
            .subscribe(() => {
                this.toggleExpanded(false);
            });
    }

    public ngAfterViewInit(): void {
        if (this.actionSheetTemplate) {
            this.actionSheet = new ActionSheet({
                templateRef: this.actionSheetTemplate
            });
        }
    }

    public ngOnDestroy(): void {}

    public abstract handleInitialExpand(): Promise<void>;

    public async toggleExpanded(expand?: boolean, updateService = true): Promise<void> {
        if (typeof expand !== 'undefined') {
            this.isExpanded = expand;
        } else {
            this.isExpanded = !this.isExpanded;
        }

        if (!this.isExpanded) {
            this.innerTableIsLoading = true;
        }

        if (updateService && this.expandableRowsService) {
            if (this.isExpanded) {
                this.expandableRowsService.expand(this.row);
            } else {
                this.expandableRowsService.collapse(this.row);
            }
        }

        if (this.isExpanded) {
            if (this.isSmallDevice) {
                this.actionSheetService.showLoading();
            }

            if (!this.dataSourceInner) {
                await this.handleInitialExpand();
            }

            Utilities.executeAfterRender(() => {
                this.innerTableIsLoading = false;
            });

            if (this.isSmallDevice) {
                this.actionSheetService.showActionSheet(this.actionSheet);
            }
        } else {
            if (this.isSmallDevice && this.actionSheetIsOpen) {
                this.actionSheetService.closeActionSheet();
            }
        }
    }

    public handleShowHideClick(event: Event): void {
        event.stopPropagation();
        this.toggleExpanded();
    }

    public handleCheck(isChecked: boolean): void {
        if (isChecked) {
            this.checkableRowsService.check(this.row[this.rowPrimaryKey]);
        } else {
            this.checkableRowsService.uncheck(this.row[this.rowPrimaryKey]);
        }
        this.check.emit(isChecked);
    }

    public handleMobileDrawerOpenedChange(isOpened: boolean): void {
        // Let the service know that the expandable row is collapsed when we close the drawer.
        if (!isOpened && this.expandableRowsService) {
            this.expandableRowsService.collapse(this.row);
        }
    }

    public trackByIndex(index: number): number {
        return index;
    }

    public handleRowClick(event: Event): void {
        this.toggleExpanded();
    }

    public handleActionCellClick(event: Event): void {
        event.stopPropagation();
    }
}
